/**
 * Les **vidéos jalons** — SPEC-MOTEUR §9.3, PLAN §4.13. Tâche M3.1.
 *
 * *« On n'atteint pas le million en publiant, on l'atteint par les leviers : jets viraux,
 * **vidéos jalons** (effets en % des abonnés actuels), rente evergreen, momentum. »*
 * (PLAN §4.4)
 *
 * ── Ce que ce module répare ────────────────────────────────────────────────────
 *
 * `content/milestones.json` porte les jalons (onze depuis ⟦A-115 §2⟧) et c'est un fichier
 * **obligatoire** du loader
 * — mais `EngineContent` n'avait **aucun champ `milestones`** : le moteur ne pouvait pas
 * structurellement lire un jalon. L'action `video_jalon` coûtait **64 h (4 jours) + 45 ⚡**,
 * n'était validée par aucune condition, et tombait dans le `default:` de la file. Aggravant :
 * elle était traitée comme une production à deux endroits — malus de crunch −5 SM et tirage
 * d'événement. **Le joueur payait 4 jours, 45 ⚡, encaissait le crunch, subissait un tirage
 * d'événement, et n'obtenait rien.** Et toute la cascade était morte avec :
 * `state.jalons_done` jamais écrit, `crediterJalon` sans appelant, l'événement `jalon_done`
 * jamais émis, les codes `jalon_conditions_not_met` / `jalon_already_done` jamais levés, et la
 * ligne « jalons » du score de victoire figée à 0/200.
 *
 * ── Pourquoi un jalon ne crée PAS de `VideoRecord` ─────────────────────────────
 *
 * C'est la question qui décide de tout le reste, et la réponse vient du PLAN §4.13 :
 * *« effets exprimés en % des abonnés actuels, **jamais en valeurs absolues** »*. Un jalon
 * n'a donc pas de `VuesPic`, pas de courbe, pas de taux de conversion — le % **EST** l'effet.
 * Lui inscrire une courbe en plus reviendrait à le payer deux fois, et le Draw My Life
 * (+30 à +80 % d'abonnés) deviendrait un bouton « gagner la partie ».
 *
 * ⚠️ **Une conséquence méritait un choix explicite, et le voici ✎.** Un jalon réussi écrit
 * `counters.last_publication_h` : il **rompt le silence**. Sans ça, le levier censé
 * accélérer la course ferait *décroître* le momentum pendant ses quatre jours (§2.4 pas 4) —
 * le joueur serait puni d'avoir joué l'accélérateur. En revanche il **n'incrémente pas**
 * `videos_published` : ce compteur pilote l'escalier de déverrouillage ⟦A-9⟧, et l'y toucher
 * déplacerait des ouvertures calibrées, en silence. Deux compteurs, deux sens, deux décisions.
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * `EngineContent.milestones` est **optionnel** : un contenu qui l'omet n'a aucun jalon
 * disponible et `video_jalon` est refusée en `jalon_conditions_not_met` — visible dans les
 * tests plutôt que silencieusement permissif. `state.jalons_rates` est optionnel lui aussi :
 * `undefined` ⇔ aucune tentative ratée, donc aucun état sérialisé à migrer. Et les ancres
 * n'appellent que `computeVideoOutcome`, qui ne sait rien des jalons.
 *
 * ── ⟦A-22⟧ Un jalon raté est CONSOMMÉ — une tentative, réussie ou non ──────────
 *
 * Mesuré sur la campagne d'équilibrage : un jalon raté n'entrait pas dans `jalons_done`, ne
 * portait qu'un cooldown de 14 jours, et versait chaque fois la **moitié d'un pourcentage des
 * abonnés du moment**. 85 tentatives sur l'horizon, ×467 composé, et le seul profil capable de
 * garder la rente était celui qui **ratait tout** : 75,2 % de victoires en exécution faible,
 * **0 sur 575** en exécution moyenne ou forte. Le jeu ne se gagnait qu'en jouant mal.
 *
 * La décision de Mathieu ne retire pas la consolation (le ÷2 de §9.3 reste) : elle retire la
 * **répétition**. Un jalon raté part dans `jalons_rates` et ne revient jamais — *« on ne refait
 * pas sa première face cam trois fois »*. Les deux listes restent distinctes parce que
 * `jalons_done` décide des **fins de victoire** (`fins.ts`, La Rue exige ≥ 2 jalons **réussis**)
 * et de la ligne « jalons » du score : « tenté » n'a jamais valu « réussi ».
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système ; le seul aléa passe par `draw` sur
 * le purpose **`jalon_params`**, déjà déclaré dans l'enum fermé du §3.2.
 */
import { jour } from "./clock.js";
import { applyEffectsInPlace } from "./effects.js";
import { flagPose } from "./events.js";
import { systemes } from "./menage.js";
import { appliquerGameOver, resoudreExcuses } from "./gameover.js";
import { crediterJalon, PC_PAR_JALON, SEUIL_JALON_REUSSI } from "./progress.js";
import { draw } from "./rng.js";
import { InvalidAction } from "./types.js";
/** Les jalons du contenu, ou un vivier vide si le fichier n'est pas fourni. */
export function jalonsDuContenu(content) {
    // ⟦C60 · A-177⟧ Jalons COUPÉS : aucun jalon n'existe plus (ni à lire, ni à jouer, ni à valider).
    // Les paliers d'abonnés, eux, restent : ils ne passent pas par ici (`progress.ts`).
    if (!systemes(content).jalons)
        return [];
    return (content.milestones ?? []);
}
/** Le jalon portant cet id, s'il existe dans le contenu. */
export function jalonParId(content, id) {
    return jalonsDuContenu(content).find((j) => j.id === id);
}
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ✎ Part de l'effet conservée par un jalon **raté** — §9.3 : « pas de PC, effet ÷2 ».
 * Un jalon raté n'est donc pas un néant : le public a quand même vu la vidéo.
 *
 * ⟦A-22⟧ **La consolation reste, la répétition disparaît.** Le ÷2 n'était pas la faille : la
 * faille était de pouvoir l'encaisser tous les 14 jours, indéfiniment, sur un pourcentage des
 * abonnés **actuels** (×467 composé sur l'horizon — `docs/AUDIT-EQUILIBRAGE.md` §6.1 bis).
 */
export const JALON_EFFET_RATE = 0.5;
/**
 * ⟦C7⟧ **La vidéo d'excuses** — l'id du jalon qui déclenche la règle §11.2.
 *
 * `resoudreExcuses` (`gameover.ts`) était écrite, testée, exportée… et **sans aucun appelant
 * de production** : la vidéo d'excuses ne restituait JAMAIS les 80 % de dégâts
 * (`EXCUSES_RESTITUTION`), alors que PLAN §4.11 en fait l'une des trois sorties de l'arc de la
 * chute. C'est le filet, et il n'existait pas.
 *
 * **Pourquoi le jalon et pas un choix d'événement.** §11.2 dit « c'est un choix d'événement »,
 * mais les deux choix de contenu qui s'appellent « excuses » (`tweet_exhume/excuses_rapides`,
 * `sponsor_arnaque_revele/excuses`) portent leurs propres effets locaux et ne sont pas *la*
 * vidéo d'excuses : ce sont des réactions à chaud, précisément ce que §11.2 oppose au fait de
 * « laisser retomber ». Le contenu, lui, écrit bel et bien une **vidéo** d'excuses — le jalon
 * `video_excuses`, gated sur le flag `arc_bad_buzz`, second de la liste. C'est une vidéo,
 * ça se produit, ça coûte quatre jours : ça correspond mot pour mot à PLAN §4.11.
 *
 * ⚠️ Entorse assumée à « contenu = données », la même qu'⟦A35⟧ : un id de contenu lu par le
 * moteur, en constante exportée et testée contre `content/milestones.json`.
 */
export const JALON_EXCUSES = "video_excuses";
/** Nœud *Gestion de crise* — la condition de réussite des excuses (§11.2). */
export const NOEUD_GESTION_DE_CRISE = "gestion_de_crise";
// ─────────────────────────────────────────────────────────────────────────────
// Conditions (§9.3, PLAN §4.13)
// ─────────────────────────────────────────────────────────────────────────────
/** Le jalon a-t-il été **réussi** ? `jalons_done` ne contient que les réussis (§9.4). */
export function jalonReussi(state, id) {
    return state.jalons_done.includes(id);
}
/**
 * Le jalon a-t-il déjà été **tenté** — réussi *ou* raté ? ⟦A-22⟧
 *
 * C'est cette question, et non « réussi ? », qui décide de la jouabilité : *« on ne refait pas
 * sa première face cam trois fois »*. Les deux listes restent séparées parce qu'elles ne
 * répondent pas à la même question (voir `GameState.jalons_rates`).
 */
export function jalonDejaTente(state, id) {
    return state.jalons_done.includes(id) || (state.jalons_rates ?? []).includes(id);
}
/**
 * Ce qui manque pour jouer ce jalon — **liste vide ⇔ jouable**. Rendue plutôt qu'un booléen
 * parce que le client doit pouvoir écrire « il te manque *Motion design* et 10 000 abonnés »
 * sans re-dériver la règle (contrat de PASSATION §3 : ne jamais recalculer une règle côté
 * client).
 *
 * ⟦A-123 · C9 vague 0⟧ Cinq lectures de plus depuis `docs/CATALOGUE-VIDEOS-SPECIALES.md` :
 * `videos_published_min`, `money_min`, `reputation_min`, `clock_h_min` (une durée, en heures
 * de calendrier simulé depuis le début de la partie) et `jalon_precedent` (un jalon
 * précédent déjà RÉUSSI — `jalons_done`, jamais seulement tenté). Chacune absente ⇔ aucune
 * vérification : l'état neutre par construction, aucune des dix jalons déjà servis ne les pose.
 */
export function conditionsManquantes(state, jalon) {
    const manque = [];
    const c = jalon.conditions;
    // Le palier compte comme franchi s'il est dans l'historique OU si les abonnés y sont : une
    // run reprise au jalon (§11.6) ne doit pas perdre l'accès à ce qu'elle avait déjà ouvert.
    const palierOuvert = state.subscribers >= c.palier_min ||
        state.milestones_reached.includes(c.palier_min);
    if (!palierOuvert)
        manque.push(`palier:${state.subscribers}/${c.palier_min}`);
    for (const skill of c.skills) {
        if (!state.skills.includes(skill))
            manque.push(`skill:${skill}`);
    }
    // ⟦A-115 §2⟧ `flagPose` — pas `hasContentFlag` seul. Les dix premiers jalons ne lisaient
    // jamais qu'un flag de CONTENU (`arc_bad_buzz`), et `hasContentFlag` (qui ne regarde que le
    // sidecar `state.content_effects.flags`) suffisait. La 11ᵉ est la première à gater sur un
    // flag MOTEUR (`fil_harcelement_vecu`, posé dans `state.flags` par `majHarcelement`,
    // `engine/fil.ts`) — `hasContentFlag` le manque toujours (il ne regarde jamais `state.flags`),
    // ce que `events.ts` avait déjà résolu pour SES propres `flags_requis` en passant par
    // `flagPose` (moteur dérivé → `state.flags` → sidecar de contenu, dans cet ordre). Les jalons
    // avaient leur propre lecture, plus étroite, jamais alignée : ce n'était pas un manque
    // visible tant qu'aucun jalon ne lisait un flag moteur.
    for (const flag of c.flags_requis) {
        if (!flagPose(state, flag))
            manque.push(`flag:${flag}`);
    }
    if (c.image_min !== undefined && state.channel_image < c.image_min) {
        manque.push(`image:${state.channel_image}/${c.image_min}`);
    }
    // ⟦A-123 · C9 vague 0⟧ Les cinq extensions du catalogue — chacune absente ⇔ aucune
    // vérification, l'état neutre par construction (CLAUDE.md). Même convention de message que
    // les quatre lectures ci-dessus : `<mot>:<valeur actuelle>/<seuil>` ou `<mot>:<id>`.
    if (c.videos_published_min !== undefined && state.counters.videos_published < c.videos_published_min) {
        manque.push(`videos:${state.counters.videos_published}/${c.videos_published_min}`);
    }
    if (c.money_min !== undefined && state.money < c.money_min) {
        manque.push(`argent:${state.money}/${c.money_min}`);
    }
    if (c.reputation_min !== undefined && state.reputation < c.reputation_min) {
        manque.push(`reputation:${state.reputation}/${c.reputation_min}`);
    }
    if (c.clock_h_min !== undefined && state.clock_h < c.clock_h_min) {
        manque.push(`duree:${jour(state.clock_h)}/${jour(c.clock_h_min)}`);
    }
    if (c.jalon_precedent !== undefined && !state.jalons_done.includes(c.jalon_precedent)) {
        manque.push(`jalon:${c.jalon_precedent}`);
    }
    return manque;
}
/** Les jalons du contenu, triés par `ordre`, avec leur état — pour l'écran Jalons. */
export function jalonsLisibles(state, content) {
    return [...jalonsDuContenu(content)]
        .sort((a, b) => a.ordre - b.ordre)
        .map((j) => {
        const fait = jalonDejaTente(state, j.id);
        const manque = conditionsManquantes(state, j);
        return {
            id: j.id,
            nom: j.nom,
            ordre: j.ordre,
            fait,
            reussi: jalonReussi(state, j.id),
            jouable: !fait && manque.length === 0,
            manque,
        };
    });
}
/**
 * Validation de `video_jalon` (§2.2). Lève `InvalidAction` **sans rien consommer** — la règle
 * du §2.3 : une action invalide ne coûte ni `turn` ni tirage.
 *
 * Rend le jalon validé, pour que l'appelant ne le recherche pas deux fois.
 */
export function validateVideoJalon(state, jalonId, content) {
    const jalon = jalonParId(content, jalonId);
    if (!jalon) {
        throw new InvalidAction("jalon_conditions_not_met", { jalon_id: jalonId, raison: "inconnu" });
    }
    // ⟦A-22⟧ « déjà fait » couvre désormais « déjà **tenté** » : le code d'erreur ne change pas
    // (il est dans l'enum fermé de SPEC-API §3.3), mais il se lève aussi après un échec.
    if (jalonDejaTente(state, jalonId)) {
        throw new InvalidAction("jalon_already_done", {
            jalon_id: jalonId,
            reussi: jalonReussi(state, jalonId),
        });
    }
    const manque = conditionsManquantes(state, jalon);
    if (manque.length > 0) {
        throw new InvalidAction("jalon_conditions_not_met", {
            jalon_id: jalonId,
            raison: "conditions",
            manque: manque.join(","),
        });
    }
    return jalon;
}
/**
 * Score d'exécution d'un jalon — **la moyenne des étapes jouées**, comme une longue.
 *
 * L'Atelier d'un jalon est « standard » en v1 (PLAN §5 : les Ateliers spéciaux — le Draw My
 * Life qui se dessine, l'interview qui se mène — sont post-v1). Le format est `longue`, donc
 * les cinq étapes comptent. `null` sur une étape = elle n'a pas été jouée : on ne l'invente
 * pas, on l'ignore, exactement comme un short ignore miniature et promotion.
 */
export function executionJalon(scores) {
    const jouees = [scores.ecriture, scores.tournage, scores.montage, scores.miniature, scores.promotion]
        .filter((v) => v !== null && v !== undefined);
    if (jouees.length === 0)
        return 0;
    return jouees.reduce((a, b) => a + b, 0) / jouees.length;
}
/**
 * **Le pipeline du jalon.** MUTE `state` (le clone de travail de la file §2.3).
 *
 * L'ordre suit §9.3 : exécution → réussite ou échec → % tiré dans la fourchette du contenu →
 * effets → PC. Le % est tiré sur `jalon_params`, le purpose que le §3.2 réserve exactement à
 * ça et qui n'avait jamais été consommé.
 */
export function jouerJalon(state, jalon, executionScore, seed, events = [], log = []) {
    const reussi = executionScore >= SEUIL_JALON_REUSSI;
    // Le % dans la fourchette du contenu (ex. Draw My Life : +30 à +80 %), puis ÷2 si raté.
    const u = draw(seed, state.turn, "jalon_params", 0);
    const { subs_pct_min: min, subs_pct_max: max } = jalon.effets_reussite;
    const brut = min + u * (max - min);
    const subsPct = reussi ? brut : brut * JALON_EFFET_RATE;
    const abonnesAvant = state.subscribers;
    // PLAN §4.13 : « en % des abonnés actuels, jamais en valeurs absolues ».
    //
    // ⚠️ **Pourquoi ce gain ne passe PAS par `applyEffects`.** Le type d'effet `subs_pct` du
    // §2.3 est borné à ±15 par Zod — c'est le plafond « aucun ÉVÉNEMENT isolé > −15 % » du
    // PLAN §4.9. Un jalon, lui, est calibré en dizaines de pour cent (le Draw My Life : +30 à
    // +80). Le faire passer par là le rognerait silencieusement de +80 % à +15 %, soit très
    // exactement la dérive muette qu'⟦A-10⟧ interdit. Le plafond des événements ne s'applique
    // donc pas aux jalons — ce sont deux mécaniques, et c'est écrit ici pour qu'on le sache.
    state.subscribers = Math.max(0, Math.round(abonnesAvant * (1 + subsPct / 100)));
    const r = applyEffectsInPlace(state, reussi ? jalon.effets_reussite.autres : jalon.effets_echec, "jalon", { source_id: jalon.id });
    events.push(...r.events);
    log.push(...r.log);
    let pcGagnes = r.pc_gained;
    if (reussi) {
        state.jalons_done.push(jalon.id);
        // §9.3 — +2 PC pour un jalon réussi. `crediterJalon` était exportée sans aucun appelant.
        const credite = crediterJalon(state, executionScore);
        if (credite)
            pcGagnes += PC_PAR_JALON;
        // ✎ Le jalon rompt le silence (voir l'en-tête) — mais n'entre pas dans `videos_published`.
        state.counters.last_publication_h = state.clock_h;
        // ⟦A-130 · C12⟧ Réussi ⇒ rompt aussi le silence du Fantôme (voir plus bas pour le cas
        // raté) : les deux compteurs avancent ensemble ici, comme une publication le ferait.
        state.counters.last_activity_h = state.clock_h;
    }
    else {
        // ⟦A-22⟧ **Un jalon raté est consommé quand même.** Il verse toujours sa moitié d'effet,
        // et il ne revient jamais : une tentative par jalon, réussie ou ratée. Ce qui cassait
        // n'était pas la consolation, c'était la **répétition** — 85 tentatives sur l'horizon,
        // chacune sur un pourcentage des abonnés du moment, ×467 composé, et le seul profil qui en
        // profitait était celui qui ratait tout (`docs/AUDIT-EQUILIBRAGE.md` §6.1 bis).
        state.jalons_rates = [...(state.jalons_rates ?? []), jalon.id];
        // ⟦A-130 · C12⟧ « Une chaîne qui essaie n'est pas morte […] elle marche pas » (Mathieu,
        // 26/08) : un jalon RATÉ rompt désormais le silence que lit le Fantôme (voie « jamais
        // décollé », ⟦A-58 · A-79⟧) — diagnostic ⟦A-128⟧, seule cette voie plafonnait la survie
        // des prudents à 0,9167 pendant qu'ils tentaient des jalons. **Et rien de plus** :
        // `last_publication_h` (régularité — momentum, decay de Hype, rente) reste intact, donc un
        // jalon raté ne devient jamais une publication. Voir `last_activity_h` (`types.ts`) et
        // `heuresDeSilenceFantome` (`sleep.ts`) pour la distinction des deux compteurs.
        state.counters.last_activity_h = state.clock_h;
    }
    // ⟦C7⟧ §11.2 — LA VIDÉO D'EXCUSES. La règle vit dans `gameover.ts` depuis M1.16 et
    // n'était appelée nulle part. Elle s'ajoute aux effets du contenu au lieu de les remplacer :
    // le contenu décide de ce que la vidéo raconte, le moteur de ce que « s'excuser » fait à
    // des dégâts subis. Les deux moitiés existaient ; il manquait la ligne qui les joint.
    let excuses = null;
    if (jalon.id === JALON_EXCUSES) {
        // §11.2 : réussies si *Gestion de crise* est possédé ET si l'on a « laissé retomber »
        // (≥ 32 h après le bad buzz — c'est `resoudreExcuses` qui mesure le délai). On y ajoute
        // la seule chose que le jalon apporte : il faut aussi avoir bien joué la vidéo.
        excuses = resoudreExcuses(state, reussi && state.skills.includes(NOEUD_GESTION_DE_CRISE), events);
        if (excuses.game_over && state.status === "active") {
            // §11.2, et `verifierGameOver` le dit déjà en toutes lettres : « seules des excuses
            // ratées pendant l'arc le font, et c'est `resoudreExcuses` qui le signale ». La
            // signalisation existait ; personne ne l'écoutait. Le chemin reste TÉLÉGRAPHIÉ et
            // ÉVITABLE (PLAN §4.11) : l'ouverture de l'arc émet son `warning`, la vidéo d'excuses
            // est un jalon qu'on choisit de jouer, et la réussir la referme.
            appliquerGameOver(state, "spirale_reputation", events);
        }
    }
    const abonnesGagnes = state.subscribers - abonnesAvant;
    events.push({
        kind: "jalon_done",
        data: {
            jalon_id: jalon.id,
            nom: jalon.nom,
            reussi,
            execution_score: Math.round(executionScore * 10) / 10,
            subs_pct: Math.round(subsPct * 10) / 10,
            abonnes_gagnes: abonnesGagnes,
            pc_gagnes: pcGagnes,
        },
    });
    log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        famille: "chaine",
        text: reussi
            ? `« ${jalon.nom} » : +${abonnesGagnes.toLocaleString("fr-FR")} abonnés (+${Math.round(subsPct)} %).`
            : `« ${jalon.nom} » n'était pas au niveau. L'effet est divisé par deux, et cette vidéo-là ne se retourne pas.`,
    });
    return {
        jalon_id: jalon.id,
        reussi,
        execution_score: executionScore,
        subs_pct: subsPct,
        abonnes_gagnes: abonnesGagnes,
        pc_gagnes: pcGagnes,
        excuses,
    };
}
/**
 * Hook `effetAction` de `applyAction` pour `video_jalon`. Rend `true` quand l'action a été
 * traitée.
 *
 * Le plafond de compétence ⟦A-9⟧ s'applique aux scores **avant** la moyenne : un jalon n'est
 * pas une dérogation au niveau du joueur (`scoresEffectifs` fait la même chose pour une
 * publication ordinaire).
 */
export function effetActionJalon(ctx, action, plafonner) {
    if (action.type !== "video_jalon")
        return false;
    const jalon = validateVideoJalon(ctx.state, action.jalon_id, ctx.content);
    jouerJalon(ctx.state, jalon, executionJalon(plafonner(action.scores)), ctx.seed, ctx.events, ctx.log);
    return true;
}
//# sourceMappingURL=jalons.js.map